import { userIcon } from "../../../../assets/images";
import dayjs from "dayjs";
import { UiButton, UiDrawer, UiProfileModel, UiWarningRating } from "../../../../components"
import { formatPhoneNumber, markdownToHtml } from "../../../../utils/appUtils";
import { useReactToPrint } from "react-to-print";
import React, { useRef } from "react";
import AgreementReport from "../report/agreementReport";


const getAgeFromDateOfBirth = (dateOfBirth) => {
    if (!dateOfBirth) {
        return null;
    }
    let parsedBirthDate = dayjs(dateOfBirth);
    if (!parsedBirthDate?.isValid()) {
        const dateOfBirthText = String(dateOfBirth);
        const dateParts = dateOfBirthText?.split(/[-/]/);
        if (dateParts?.length === 3) {
            const [firstPart, secondPart, thirdPart] = dateParts;
            const normalizedDate = firstPart?.length === 4
                ? `${firstPart}-${secondPart}-${thirdPart}`
                : `${thirdPart}-${secondPart}-${firstPart}`;
            parsedBirthDate = dayjs(normalizedDate);
        }
    }
    if (!parsedBirthDate?.isValid() || parsedBirthDate?.isAfter(dayjs(), "day")) {
        return null;
    }
    const today = dayjs();
    const currentYearBirthday = parsedBirthDate?.year(today?.year());
    const hasBirthdayPassed = today?.isSame(currentYearBirthday, "day") || today?.isAfter(currentYearBirthday, "day");
    let calculatedAge = today?.year() - parsedBirthDate?.year();
    if (!hasBirthdayPassed) {
        calculatedAge -= 1;
    }
    return calculatedAge >= 0 ? calculatedAge : null;
};

const CustomerView = ({
    customerOpenCloseAction,
    handleCustomerProfileClose,
    customerByIdDate,
    userStatus,
    agreementMd,
}) => {
    const data = customerByIdDate?.data;
    const customerAge = getAgeFromDateOfBirth(data?.dateOfBirth);
    const firstBlockInfo = data?.blockInfo?.[0];
    const blockDays = Number(firstBlockInfo?.blockDays);
    const hasBlockInfoValues = Number.isFinite(blockDays) && blockDays > 0;

    const agreementRef = useRef();
    const salutation = data?.salutation || "";
    const firstName = data?.firstName || "";
    const middleName = data?.middleName || "";
    const lastName = data?.lastName || "";
    const fullName = [salutation, firstName, middleName, lastName].filter(Boolean).join(" ");

    const processedMd = (agreementMd || "").replace(/\*\{\{NAME\}\}\*/g, `**${fullName}**`).replace(/\{\{NAME\}\}/g, `**${fullName}**`);
    const agreementHtml = markdownToHtml(processedMd);

    const handleAgreementPrint = useReactToPrint({
        content: () => agreementRef.current,
        documentTitle: "Client User Agreement",
        pageStyle: `
            body {
                -webkit-print-color-adjust: exact;
                print-color-adjust: exact;
            }
            .approval-Report::before {
                content: "";
                position: fixed;
                top: 0;
                left: 0;
                right: 0;
                bottom: 0;
                border: 2px solid #005D60;
                z-index: 9999;
                pointer-events: none;
            }
        `
    });

    return <>
        <UiDrawer
            title={"Customer Profile Details"}
            open={customerOpenCloseAction?.isOpenProfileDrawer}
            onClose={handleCustomerProfileClose}
            width={600}
            footer={
                <div className="flexible-end">
                    <UiButton onClick={handleCustomerProfileClose}>Close</UiButton>
                    <UiButton type="primary" onClick={handleAgreementPrint} disabled={!agreementHtml}>Download Agreement</UiButton>
                </div>
            }
        >
            <div>
                <div className="lawyer-information">
                    <div className="flexible" style={{ borderBottom: "1px solid #000" }}>
                        <div className="flexible">
                            {
                                !customerByIdDate?.customerProfile || customerByIdDate?.customerProfile?.includes("null") || customerByIdDate?.customerProfile?.includes("undefined") ? <img src={userIcon} style={{ width: "50px", height: "50px", borderRadius: "50%", objectFit: "cover" }} alt="" /> : <UiProfileModel imageUrl={customerByIdDate?.customerProfile} />
                            }
                            <div>
                                <h3 className="lawerInfo-detail">{data?.salutation} {data?.firstName} {data?.middleName} {data?.lastName}</h3>
                                <div style={{ padding: "5px 10px" }}>
                                    <UiWarningRating count={data?.warningCountAfterActive} />
                                </div>
                            </div>
                        </div>
                        <div className="flexible">
                            <div>
                                <small className="information-header">Customer ID</small>
                                <h3 className="lawerInfo-detail">{data?.customerNo || "Nil"}</h3>
                            </div>
                        </div>
                    </div>
                    <div className="flexible">
                        <div>
                            <small className="information-header">Mobile Number</small>
                            <h3 className="lawerInfo-detail">{data?.mobileNumber ? formatPhoneNumber(data?.mobileNumber) : "Nil"}</h3>
                        </div>
                        <div>
                            <small className="information-header">E-mail Address</small>
                            <h3 className="lawerInfo-detail">{data?.email || "Nil"}</h3>
                        </div>
                    </div>
                </div>
                <div className="lawyer-details">
                    <div>
                        <div style={{ borderBottom: "1px solid #00000054", paddingBottom: "10px" }}>
                            <h3 className="lawyerInfo-heading" >Consultion Details</h3>
                        </div>
                        <div className="flexible-start">
                            <div>
                                <small className="information-header">Total Cons.</small>
                                <h3 className="lawerInfo-detail">{data?.totalConsultation || "Nil"}</h3>
                            </div>
                            <div>
                                <small className="information-header">Upcoming Cons.</small>
                                <h3 className="lawerInfo-detail">{data?.upcomingConsultation || "Nil"}</h3>
                            </div>
                            <div>
                                <small className="information-header">Cancelled Cons.</small>
                                <h3 className="lawerInfo-detail">{data?.totalCancelConsultation || "Nil"}</h3>
                            </div>
                        </div>
                    </div>
                    <div style={{ padding: "10px 0px" }}>
                        <div style={{ borderBottom: "1px solid #00000054", padding: "10px 0px" }}>
                            <h3 className="lawyerInfo-heading">Personal Details</h3>
                        </div>
                        <div className="flexible-start">
                            <div>
                                <small className="information-header">Gender</small>
                                <h3 className="lawerInfo-detail">{data?.gender || "Nil"}</h3>
                            </div>
                            <div>
                                <small className="information-header">Age</small>
                                <h3 className="lawerInfo-detail">{customerAge ?? "Nil"}</h3>
                            </div>
                        </div>
                    </div>
                    <div>
                        <div className="flexible-start">
                            <div style={{ width: "100%" }}>
                                <small className="information-header">Address</small>
                                <h3 className="lawerInfo-detail">{data?.address1 || "Nil"}</h3>
                            </div>
                        </div>
                    </div>
                    {
                        userStatus === "HOLD" && hasBlockInfoValues && (
                            <div style={{ padding: "10px 0px" }}>
                                <div style={{ borderBottom: "1px solid #00000054", padding: "10px 0px" }}>
                                    <h3 className="lawyerInfo-heading">Block Details</h3>
                                </div>
                                <div className="flexible">
                                    <div>
                                        <small className="information-header">Block Days</small>
                                        <h3 className="lawerInfo-detail">{firstBlockInfo?.blockDays ?? "Nil"}</h3>
                                    </div>
                                    <div>
                                        <small className="information-header">Block Date</small>
                                        <h3 className="lawerInfo-detail">{firstBlockInfo?.blockDate ? dayjs(firstBlockInfo?.blockDate)?.format("YYYY-MM-DD HH:mm:ss") : "Nil"}</h3>
                                    </div>
                                    <div>
                                        <small className="information-header">Reason</small>
                                        <h3 className="lawerInfo-detail">{firstBlockInfo?.reason || "Nil"}</h3>
                                    </div>
                                </div>
                            </div>
                        )
                    }
                </div>
            </div>
        </UiDrawer>
        <div style={{ display: "none" }}>
            <AgreementReport ref={agreementRef} htmlContent={agreementHtml} pdfName="Client User Agreement" />
        </div>
    </>
}
export default CustomerView